import { inject } from '@angular/core';
import { CanMatchFn } from '@angular/router';
import { LoggingService } from '@app/core/services/logging/logging.service';
import { BookingService } from '@app/core/services/backend/booking.service';
import { FirebaseAuthenticationService } from '@app/core/services/firebase/firebase-authentication/firebase-authentication.service';
import { User } from '@capacitor-firebase/authentication';
import { from, of, switchMap, map, catchError } from 'rxjs';

// Flags to track invitation processing state
let isProcessingInvitation = false;
let invitationProcessedThisSession = false;

export const invitationGuard: CanMatchFn = () => {
  const auth = inject(FirebaseAuthenticationService);
  const bookingService = inject(BookingService);

  return from(auth.getCurrentUser()).pipe(
    switchMap((user: User | undefined) => {
      if (!user || !user.email || user.isAnonymous) {
        return of(true);
      }

      const bookingInvitation = localStorage.getItem('bookingInvitation');

      // If no invitation or already processed this session, allow navigation
      if (
        !bookingInvitation ||
        invitationProcessedThisSession ||
        isProcessingInvitation
      ) {
        return of(true);
      }

      // Process the invitation
      isProcessingInvitation = true;
      const bookingInvitationData = JSON.parse(bookingInvitation);

      return bookingService
        .getBookingInvitation(bookingInvitationData.bookingId)
        .pipe(
          map((booking) => {
            localStorage.removeItem('bookingInvitation');
            isProcessingInvitation = false;
            invitationProcessedThisSession = true;
            LoggingService.log(
              'Booking invitation processed successfully',
              booking,
            );
            return true;
          }),
          catchError((error) => {
            LoggingService.log('Error getting booking invitation', error);
            const message = `core.dialog.error.bookingInvitation.${error.error.message}`;
            localStorage.setItem('pendingInvitationError', message);
            localStorage.removeItem('bookingInvitation');
            isProcessingInvitation = false;
            invitationProcessedThisSession = true; // Mark as processed to prevent retry
            return of(true);
          }),
        );
    }),
  );
};
